import { Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { CustomValue } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.styled';
import { TJobRenderComponentProps } from '@/console/features/jobs/components/jobs-list/jobs-list-renderer.types';
import { ExecuteIcon } from '@/shared/assets/icons/execute-icon';
import { PipelinesIcon } from '@/shared/assets/icons/pipelines-icon';
import { Skeleton } from '@/shared/components/ui/skeleton/skeleton.styled';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';
import { useEnumLabel } from '@/shared/hooks/use-enum-label/use-enum-label';
import { useReadPipelineQuery } from '@/shared/redux/api/jobs/jobs-api';
import { isPermissionDeniedError } from '@/shared/redux/api/utils/error-handlers';
import { JobSourceEnum } from '@/shared/types/types.generated';
import { getProtectedResourceLabel } from '@/shared/utils/protected-resource-label';

export const JobSource: React.FC<TJobRenderComponentProps> = ({
  job,
  truncate,
}) => {
  const { t } = useTranslation();
  const { getEnumLabel } = useEnumLabel();
  const navigate = useNavigate();
  const {
    data: pipelineData,
    error: pipelineError,
    isLoading: pipelineLoading,
    isFetching: pipelineFetching,
  } = useReadPipelineQuery(
    { pipelineConfigurationId: job?.pipelineConfigurationId! },
    {
      skip:
        !job?.pipelineConfigurationId || job?.source !== JobSourceEnum.Pipeline,
    },
  );

  const pipeline = pipelineData?.readPipelineConfiguration;
  const pipelinePending = pipelineLoading || pipelineFetching;
  const isPipelineProtected =
    !pipeline && !pipelinePending && isPermissionDeniedError(pipelineError);

  const navigateToPipeline = () => {
    if (!pipeline) {
      return undefined;
    }
    navigate(`/pipelines/${job?.pipelineConfigurationId}`);
  };

  const getSourceName = () => {
    if (
      job?.source === JobSourceEnum.Pipeline ||
      job?.pipelineConfigurationId
    ) {
      return (
        pipeline?.name ??
        (isPipelineProtected
          ? getProtectedResourceLabel(t, 'pipeline')
          : t('pipelines:deleted_pipeline'))
      );
    }

    if (!job?.source) {
      return t('common:manual');
    }

    return getEnumLabel(job?.source, 'pipelines:job_source');
  };

  const renderLabel = () => {
    const label = getSourceName();

    if (truncate) {
      return <TruncatedText variant="p7" maxLength={32} text={label} />;
    }

    return <Typography variant="p7">{label}</Typography>;
  };

  const getTooltipTitle = () => {
    if (pipeline?.name && (!truncate || getSourceName()?.length <= 32)) {
      return t('pipelines:open_pipeline');
    }
    return '';
  };

  if (pipelinePending) {
    return <Skeleton />;
  }

  return (
    <Tooltip title={getTooltipTitle()}>
      <CustomValue onClick={navigateToPipeline} $clickable={!!pipeline}>
        {!job?.pipelineConfigurationId ? (
          <ExecuteIcon size="1rem" variant="primary" />
        ) : (
          <PipelinesIcon
            size="1rem"
            variant={pipeline || !isPipelineProtected ? 'primary' : 'light'}
          />
        )}
        {renderLabel()}
      </CustomValue>
    </Tooltip>
  );
};
